import Button, { ButtonSize, ButtonType, ButtonVariant } from 'src/components/atoms/Button';
import Gap from 'src/components/atoms/Gap';
import { InputText, InputTextType } from 'src/components/atoms/InputText';
import { Row } from 'src/components/atoms/Row';
import Text, { TextAlign } from 'src/components/atoms/Text';
import { ControlledDropdown } from 'src/components/molecules/Dropdown';
import { ACCOUNT_TYPE_OPTIONS, PanelAccount } from 'src/features/assetVerification';
import { DropdownFill, FieldLabel, FieldPair, SectionHeading, StatementDivider } from './styles';
import StatementRow from './StatementRow';

const accountTypeData = ACCOUNT_TYPE_OPTIONS.map(({ value, label }) => ({ value, label }));

type AccountSectionProps = {
  // Per-document view: statements here are only this document's slice; the wire cap
  // counts the whole account, so the parent computes canAddStatement globally.
  account: PanelAccount;
  accountIndex: number;
  canRemoveAccount: boolean;
  canAddStatement: boolean;
  onAddStatement: () => void;
  onRemoveStatement: (statementKey: string) => void;
  onRemoveAccount: () => void;
  onResolveReasonCode: (statementKey: string, code: number) => void;
  onChangeAnalystReasonCodes: (statementKey: string, codes: number[]) => void;
  onOverrideStaleStart: (statementKey: string) => void;
};

const AccountSection = ({
  account,
  accountIndex,
  canRemoveAccount,
  canAddStatement,
  onAddStatement,
  onRemoveStatement,
  onRemoveAccount,
  onResolveReasonCode,
  onChangeAnalystReasonCodes,
  onOverrideStaleStart
}: AccountSectionProps) => {
  const prefix = `acct${accountIndex}`;

  return (
    <section data-testid={`account-section-${accountIndex}`}>
      <Row>
        <Row.Col size={8}>
          <SectionHeading>Account Information</SectionHeading>
        </Row.Col>
        <Row.Col size={4}>
          {canRemoveAccount && (
            <Button
              name={`remove-account-${accountIndex}`}
              data-testid={`remove-account-${accountIndex}`}
              type={ButtonType.button}
              size={ButtonSize.small}
              variant={ButtonVariant.ghost}
              onClick={onRemoveAccount}
            >
              Remove account
            </Button>
          )}
        </Row.Col>
      </Row>
      <FieldLabel>
        <Text align={TextAlign.left}>Account holder name</Text>
      </FieldLabel>
      <InputText
        name={`${prefix}.accountHolderName`}
        data-testid={`account-holder-name-${accountIndex}`}
        type={InputTextType.text}
        placeholder=""
        showOptionalLabel={false}
      />
      <Gap height={1} />
      <FieldLabel>
        <Text align={TextAlign.left}>Institution name</Text>
      </FieldLabel>
      <InputText
        name={`${prefix}.institutionName`}
        data-testid={`institution-name-${accountIndex}`}
        type={InputTextType.text}
        placeholder=""
        showOptionalLabel={false}
      />
      <Gap height={1} />
      <FieldPair>
        <div>
          <FieldLabel>
            <Text align={TextAlign.left}>Account type</Text>
          </FieldLabel>
          <DropdownFill>
            <ControlledDropdown
              name={`${prefix}.accountType`}
              data={accountTypeData}
              showLabel={false}
            />
          </DropdownFill>
        </div>
        <div>
          <FieldLabel>
            <Text align={TextAlign.left}>Last 4 digits</Text>
          </FieldLabel>
          <InputText
            name={`${prefix}.accountNumberLastFour`}
            data-testid={`account-last-four-${accountIndex}`}
            type={InputTextType.text}
            maxLength={4}
            placeholder=""
            showOptionalLabel={false}
          />
        </div>
      </FieldPair>
      <Gap height={1.5} />
      <SectionHeading>Statements</SectionHeading>
      {account.statements.map((statement, statementIndex) => (
        <StatementRow
          key={statement.key}
          fieldPrefix={`stmt${accountIndex}_${statementIndex}`}
          testIdSuffix={`${accountIndex}-${statementIndex}`}
          canRemove={account.statements.length > 1}
          onRemove={() => onRemoveStatement(statement.key)}
          reasonCodes={statement.reasonCodes ?? []}
          onResolveReasonCode={(code) => onResolveReasonCode(statement.key, code)}
          analystReasonCodes={statement.analystReasonCodes ?? []}
          onChangeAnalystReasonCodes={(codes) => onChangeAnalystReasonCodes(statement.key, codes)}
          isNinetyDayOverridden={statement.ninetyDayOverridden ?? false}
          onOverrideStaleStart={() => onOverrideStaleStart(statement.key)}
        />
      ))}
      <Button
        name={`add-statement-${accountIndex}`}
        data-testid={`add-statement-${accountIndex}`}
        type={ButtonType.button}
        size={ButtonSize.normal}
        variant={ButtonVariant.outline}
        isDisabled={!canAddStatement}
        onClick={onAddStatement}
      >
        + Add another statement from this account
      </Button>
      <StatementDivider />
    </section>
  );
};

export default AccountSection;
